import { LocalStorageService } from 'core/services/localStorage';
import { JUMBO_CLIENT_ID, ZIPPEDI_CLIENT_ID } from "core/utils/constants";
import { toSnakeCase } from "core/utils/parsers";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from "react-router-dom";

import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Grid, LinearProgress, Paper, Typography } from "@mui/material";
import { MultiSelector, Selector, isEmptyOrUndefined } from "@zippeditoolsjs/blocks";
import { IconComponent } from "@zippeditoolsjs/zippedi-icons";

import AlertBox from '../../tools/AlertBox';
import DateRange from "../../tools/DateRange";
import Modal from '../../tools/Modal';
import { format } from '../../utils/converters';
import { formatDataGridToExcel, formatNivoToExcel, formatToExcel } from "../../utils/parsers";
import Visualization from './Visualization';

export default function Dashboards(props) {
  const {
    isLoadingDashboard,
    dashboardInfo,
    getDashboard,
    isLoadingUserStores, // Is the same loading variable for chains, because they come from the same endpoint
    userStores,
    getUserStores,
    userChains,
    selectedClient,
    daysBeforeRange = 7,
    isLoadingExcelDownload,
    downloadExcel,
    getStoreCategories,
    categories,
    isLoadingCategories,
    getUserSuppliers,
    suppliers,
    isLoadingSuppliers,
    clientFormats,
    isLoadingClientFormats,
    getStockCategories,
    isLoadingStockCategories,
    stockCategories,
    getLastProductSources,
    isLoadingLastProductSources,
    lastProductSources,
  } = props;

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [queryParameters] = useSearchParams();
  const [dashboardForm, setDashboardForm] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [dateState, setDateState] = useState([]);
  // Filters states
  const [selectedChain, setSelectedChain] = useState(null);
  const [selectedStores, setSelectedStores] = useState(null);
  const [filteredStores, setFilteredStores] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedStoreType, setSelectedStoreType] = useState([]);
  const [selectedStockCategories, setSelectedStockCategories] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState([]);
  const [selectedProductStatus, setSelectedProductStatus] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [generalModalOpen, setGeneralModalOpen] = useState(false);
  const [selectedVisualization, setSelectedVisualization] = useState(null);
  // For the vertical bar chart in the OSA dashboard
  const [OSAVerticalBarClickedLegend, setOSAVerticalBarClickedLegend] = useState("availability_data"); // 3 options: "availability_data", "stock_available", "stock_not_available"

  const dashboardId = location.state?.dashboardId ? location.state?.dashboardId : queryParameters.get('dashboardId');
  const isOSADashboard = ['586'].includes(dashboardId) // TODO: change this to a more robust way to identify OSA dashboards
  const isOldOSADashboard = ['581'].includes(dashboardId) // TODO: delete when old OSA report is deprecated

  // TODO: remove this when we have a way to know which stores have a darkstore and which ones don't
  const store_types = [
    {
      id: 1,
      name: 'clientstore',
      parsed_name: t('cws_app.general.client_store', 'Client Store')
    },
    {
      id: 2,
      name: 'darkstore',
      parsed_name: t('cws_app.general.dark_store', 'Dark Store')
    }
  ]

  //TODO - Remove this mock and create a table in cube to get all posibles status for products
  const productStatus = [
    { 'status_name': t('cws_app.general.Product_available', 'Product Available'), 'status_id': true },
    { 'status_name': t('cws_app.general.Product_not_available', 'Product Not Available'), 'status_id': false },
  ];

  // Lifecycle methods
  useEffect(() => {
    if (!isEmptyOrUndefined(selectedClient, 'object')) {
      getUserStores(selectedClient.client_id);
      setSelectedChain(null);
      setSelectedStores(null);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (!isEmptyOrUndefined(selectedChain, 'object')) {
      getUserSuppliers(selectedClient.client_id, [selectedChain?.chain_name]);
      getStockCategories(t);
      getLastProductSources(t);
    }
  }, [selectedChain]);

  // Save the selected stores to local storage
  useEffect(() => {
    const localStorageService = new LocalStorageService();
    if (!isEmptyOrUndefined(selectedStores, 'array')) {
      localStorageService.save(localStorageService.paths.home_store, selectedStores);
    }
  }, [selectedStores]);

  // Save the selected date range to local storage
  useEffect(() => {
    const localStorageService = new LocalStorageService();
    if (!isEmptyOrUndefined(dateState, 'array')) {
      localStorageService.save(localStorageService.paths.dsh_date_range, dateState);
    }
  }, [dateState]);

  // Get selected stores from local storage
  useEffect(() => {
    const localStorageService = new LocalStorageService();
    const localStores = localStorageService.get(localStorageService.paths.home_store);
    if (!isEmptyOrUndefined(localStores, 'array') && !isEmptyOrUndefined(selectedClient, 'object')) {
      const localStoresFiltered = localStores.filter(store => (store.chain_name === selectedClient?.name) || selectedClient?.client_id === ZIPPEDI_CLIENT_ID);
      const chain = localStoresFiltered[0]?.chain_id ? userChains.find(chain => chain.chain_id === localStoresFiltered[0].chain_id) : null
      setSelectedChain(chain);
    } else {
      setSelectedChain([]);
      setSelectedStores([]);
    }
  }, [userChains]);

  // Get selected date range from local storage
  useEffect(() => {
    const localStorageService = new LocalStorageService();
    const localDateRange = localStorageService.get(localStorageService.paths.dsh_date_range);
    if (!isEmptyOrUndefined(localDateRange, 'array')) {
      // Convert the dates to Date objects
      const newDateRange = localDateRange.map(item => ({
        ...item,
        startDate: new Date(item.startDate),
        endDate: new Date(item.endDate)
      }));
      setDateState(newDateRange);
    } else {
      setDateState([
        {
          startDate: new Date(new Date().getTime() - (daysBeforeRange * 24 * 60 * 60 * 1000)),
          endDate: new Date(),
          key: 'selection'
        }])
    }
  }, []);

  // Filter the stores list based on the selected chain. If there's no chain selected, show all the stores
  useEffect(() => {
    const selectedChainID = selectedChain?.chain_id ?? userChains[0]?.chain_id;

    if (!isEmptyOrUndefined(selectedChain, 'object') && (isOSADashboard || isOldOSADashboard)) {
      const filteredStores = userStores.filter(store => store.chain_id === selectedChainID);
      setFilteredStores(filteredStores);
    } else {
      setFilteredStores(userStores);
    }

    setSelectedStores(prevState => {
      if (prevState === null) {
        return getFromLocalStorage();
      } else if (selectedChain && userChains.length > 0) {
        const localStores = getFromLocalStorage();
        return localStores.filter(store => store.chain_id === selectedChainID);
      } else {
        return [];
      }
    });
  }, [selectedChain, userChains, isOSADashboard])

  // Get categories and subcategories
  useEffect(() => {
    if (!isEmptyOrUndefined(selectedStores, 'array') && selectedStores?.[0]?.store_id !== null) {
      // Gets the categories only from the first store in the list
      getStoreCategories(selectedStores[0].store_id, selectedClient.client_id, true);
    }
  }, [selectedStores])

  // Filter subcategories based on the selected categories
  useEffect(() => {
    if (!isEmptyOrUndefined(categories, 'object') && !isEmptyOrUndefined(categories['sub_categories'], 'array') && !isEmptyOrUndefined(selectedCategories, 'array')) {
      // Keep if the category_value from the subcategories starts with the category_value from the categories
      const filteredSubCat = categories['sub_categories'].filter(category => selectedCategories.some(selectedCategory => category.category_value.startsWith(selectedCategory.category_value)));
      setFilteredSubCategories(filteredSubCat);
      setSelectedSubCategories([]);
    } else {
      setFilteredSubCategories([]);
    }
  }, [categories, selectedCategories])

  // Get dashboard data
  useEffect(() => {
    if (!isEmptyOrUndefined(selectedStores, 'array') && !isEmptyOrUndefined(selectedClient, 'object') && !isEmptyOrUndefined(dateState, 'array')) {
      const inputFormat = 'yyyy/MM/dd';
      let selectedStoresParsed = selectedStores?.map(row => row.store_code);
      let startDate, endDate;
      try {
        startDate = format(dateState[0].startDate, null, inputFormat);
        endDate = format(dateState[0].endDate, null, inputFormat);
      } catch (error) {
        console.log('error', error);
        [startDate, endDate] = [null, null];
      }

      const form = {
        dashboard_id: dashboardId,
        client_id: selectedClient.client_id,
        stores: selectedStoresParsed?.length === 0 ? userStores.map(row => row.store_code) : selectedStoresParsed,
        start_date: startDate,
        end_date: endDate
      }

      // Add supplier_id when one is selected in the tobpar
      if (!isEmptyOrUndefined(selectedClient?.supplier_id, 'id')) {
        form.suppliers = [selectedClient?.supplier_id]
      }

      // Add suppliers to the form (from the filter)
      // TODO: Fix this condition when supplier id filter is standard
      if (isOSADashboard || isOldOSADashboard) {
        form.is_old_osa = isOldOSADashboard;
        if (!isEmptyOrUndefined(selectedSuppliers, 'array')) {
          form.suppliers = selectedSuppliers.map(row => row.id);
        } else if (!isEmptyOrUndefined(selectedClient?.supplier_id, 'id')) {
          form.suppliers = [selectedClient?.supplier_id]
        }
      }

      // Add categories to the form
      if (!isEmptyOrUndefined(selectedCategories, 'array')) {
        form.categories = selectedCategories.map(row => row.category_name);
        form.category_level = selectedCategories[0].level;
      }

      // Add subcategories to the form
      if (!isEmptyOrUndefined(selectedSubCategories, 'array')) {
        form.sub_categories = selectedSubCategories.map(row => row.category_name);
        form.sub_category_level = selectedSubCategories[0].level;
      }

      // Add store types to the form
      if (!isEmptyOrUndefined(selectedStoreType, 'array')) {
        form.store_types = selectedStoreType.map(row => row.name);
      }

      // Add stock category to the form
      if (!isEmptyOrUndefined(selectedStockCategories, 'array')) {
        form.stock_categories = selectedStockCategories.map(row => row.stock_cube_id);
      }

      // Add data source to the form
      if (!isEmptyOrUndefined(selectedDataSource, 'array')) {
        form.sources = selectedDataSource.map(row => row.source_cube_id);
      }

      // Add product status to the form
      if (!isEmptyOrUndefined(selectedProductStatus, 'array')) {
        form.product_status = selectedProductStatus.map(row => row.status_id);
      }

      setDashboardForm(form);
      if (JSON.stringify(form) !== JSON.stringify(dashboardForm)) {
        getDashboard(form);
      }
    }
  }, [selectedStores, dateState, dashboardId, userStores, selectedSuppliers, selectedCategories, selectedSubCategories, selectedStoreType, selectedStockCategories, selectedDataSource, selectedProductStatus])

  // Methods

  const getFromLocalStorage = () => {
    const localStorageService = new LocalStorageService();
    const localStores = localStorageService.get(localStorageService.paths.home_store);
    let localStoresFiltered = [];
    if (!isEmptyOrUndefined(localStores, 'array') && !isEmptyOrUndefined(selectedClient, 'object')) {
      localStoresFiltered = localStores.filter(store => (store.chain_name === selectedClient?.name) || selectedClient?.client_id === ZIPPEDI_CLIENT_ID);
    }
    return localStoresFiltered;
  }

  const handleScroll = (event) => {
    setScrollPosition(event.currentTarget.scrollLeft);
  };

  const handleDownload = (raw_data) => {
    let excelDataList = []
    let dashboardTitles = []

    if (dashboardInfo?.visualizations) {
      // Add data for each chart
      dashboardInfo.visualizations.forEach(visualization => {
        if (visualization.visualization_type !== 'osa_kpi_metric') {
          dashboardTitles.push(toSnakeCase(t(`cws_app.dashboards.${visualization.visualization_name}`, visualization.visualization_name ? visualization.visualization_name : 'Dashboard')))
          if (raw_data) {
            let newOptionsRaw = downloadOptionsRaw(visualization)
            excelDataList.push(newOptionsRaw);
          }
          else {
            excelDataList.push(downloadOptions(visualization))
          }
        }
      })
    }

    const excelDate = format(new Date(), i18n.language, clientFormats.date_format);
    downloadExcel(excelDataList,
      toSnakeCase(t(`cws_app.dashboards.${dashboardInfo?.dashboardTitle}`,
        dashboardInfo?.dashboardTitle ? dashboardInfo?.dashboardTitle : 'Dashboard')),
      false,
      true,
      dashboardTitles,
      excelDate,
    );
    setGeneralModalOpen(false);
  }

  const handleIndividualDownload = (raw_data) => {
    let excelDataList = []
    if (raw_data) {
      excelDataList = downloadOptionsRaw(selectedVisualization);
    }
    else {
      excelDataList = downloadOptions(selectedVisualization);
    }

    if (excelDataList?.length) {
      const excelDate = format(new Date(), i18n.language, clientFormats.date_format);
      downloadExcel(excelDataList,
        toSnakeCase(t(`cws_app.dashboards.${selectedVisualization.visualization_name}`,
          selectedVisualization.visualization_name ? selectedVisualization.visualization_name : 'Dashboard')),
        false,
        false,
        excelDate);
      setModalOpen(false);
    } else {
      console.warn(t('cws_app.dashboards.no_data', 'No data to download'))
    }
  }

  const checkDownloadDisabled = (visualization) => {
    const excelDataList = downloadOptions(visualization);
    return excelDataList?.length === 0;
  }

  // Raw data download
  const downloadOptionsRaw = (visualization) => {
    let excelDataList = [];

    // TODO: remove "if" code when Analytics migrates to the new cubes and we can add all the dimensions to one query in the DB (table "metrics")
    if (!isEmptyOrUndefined(visualization, 'object')) {
      if (visualization?.chart_params?.length > 1) {

        // Flat the data to one array
        let flatData = [];
        if (visualization.visualization_type === 'osa_vertical_bar_chart') {
          flatData = visualization?.chart_params?.flatMap(chart => chart?.[OSAVerticalBarClickedLegend]?.raw_data);
        } else {
          flatData = visualization?.chart_params?.flatMap(chart => chart.raw_data);
        }

        const mergedObjects = {};

        for (const obj of flatData) {
          const { creationTimestamp, ...rest } = obj;

          if (!mergedObjects[creationTimestamp]) {
            // If the merged object doesn't exist, create it.
            mergedObjects[creationTimestamp] = { creationTimestamp, ...rest };
          } else {
            // If the merged object already exists, merge the properties.
            Object.assign(mergedObjects[creationTimestamp], rest);
          }
        }

        const result = Object.values(mergedObjects);

        // Format the data to excel
        if (visualization.visualization_type === 'osa_vertical_bar_chart') {
          excelDataList = formatToExcel(visualization?.chart_params?.[0]?.[OSAVerticalBarClickedLegend]?.raw_data, visualization.chart_params[0]?.[OSAVerticalBarClickedLegend]?.chart_target_x)
        } else {
          excelDataList = formatToExcel(result, visualization.chart_params[0].chart_target_x)
        }
      } else {
        if (visualization.visualization_type === 'osa_vertical_bar_chart') {
          excelDataList = formatToExcel(visualization?.chart_params?.[0]?.[OSAVerticalBarClickedLegend]?.raw_data, visualization.chart_params[0]?.[OSAVerticalBarClickedLegend]?.chart_target_x)
        } else {
          excelDataList = formatToExcel(visualization?.chart_params?.[0].raw_data, visualization.chart_params[0].chart_target_x)
        }
      }
    }

    if (excelDataList?.length > 0) {

      // Replace the column name "cat_0" with "main_category"
      if (excelDataList?.[0].includes("cat_0")) {
        excelDataList[0] = excelDataList?.[0].map(x => x === 'cat_0' ? 'main_category' : x);
      }

      // Replace the column name "mainCategory" with "main_category"
      if (excelDataList?.[0].includes("mainCategory")) {
        excelDataList[0] = excelDataList?.[0].map(x => x === 'mainCategory' ? 'main_category' : x);
      }

      // Replace the column name "cat_1" with "sub_category"
      if (excelDataList?.[0].includes("cat_1")) {
        excelDataList[0] = excelDataList?.[0].map(x => x === 'cat_1' ? 'sub_category' : x);
      }

      // Replace the column name "subCategory" with "sub_category"
      if (excelDataList?.[0].includes("subCategory")) {
        excelDataList[0] = excelDataList?.[0].map(x => x === 'subCategory' ? 'sub_category' : x);
      }

      // Translate the header
      excelDataList[0] = excelDataList[0].map(header => t(`cws_app.dashboards.${header}`, header))
    }

    return excelDataList;
  }

  // Summarized data download
  const downloadOptions = (visualization) => {
    let excelDataList = [];
    if (!isEmptyOrUndefined(visualization, 'object')) {
      if (visualization.visualization_type === 'heatmap') {
        excelDataList = formatNivoToExcel(visualization?.chart_params?.[0]?.data, visualization?.chart_params?.[0].chart_target_x)
      } else if (visualization.visualization_type === 'line_chart' || visualization.visualization_type === 'bar_chart' || visualization.visualization_type === 'osa_vertical_bar_chart') {
        // TODO: remove "if" code when Analytics migrates to the new cubes and we can add all the dimensions to one query in the DB (table "metrics")
        if (visualization?.chart_params?.length > 1) {
          // Flat the data to one array
          let flatData = [];
          if (visualization.visualization_type === 'line_chart') {
            flatData = visualization?.chart_params?.flatMap(chart => chart.line_data);
          } else if (visualization.visualization_type === 'osa_vertical_bar_chart') {
            flatData = visualization?.chart_params?.flatMap(chart => chart?.[OSAVerticalBarClickedLegend]?.bar_data);
          } else {
            flatData = visualization?.chart_params?.flatMap(chart => chart.bar_data);
          }

          const mergedObjects = {};

          for (const obj of flatData) {
            const { creationTimestamp, ...rest } = obj;

            if (!mergedObjects[creationTimestamp]) {
              // If the merged object doesn't exist, create it.
              mergedObjects[creationTimestamp] = { creationTimestamp, ...rest };
            } else {
              // If the merged object already exists, merge the properties.
              Object.assign(mergedObjects[creationTimestamp], rest);
            }
          }

          const result = Object.values(mergedObjects);
          excelDataList = formatToExcel(result, visualization.chart_params[0].chart_target_x)
        } else {
          if (visualization.visualization_type === 'line_chart') {
            excelDataList = formatToExcel(visualization?.chart_params?.[0].line_data, visualization.chart_params[0].chart_target_x)
          } else if (visualization.visualization_type === 'osa_vertical_bar_chart') {
            excelDataList = formatToExcel(visualization?.chart_params?.[0]?.[OSAVerticalBarClickedLegend]?.bar_data, visualization.chart_params[0]?.[OSAVerticalBarClickedLegend]?.chart_target_x)
          } else {
            excelDataList = formatToExcel(visualization?.chart_params?.[0].bar_data, visualization.chart_params[0].chart_target_x)
          }
        }
      } else if (visualization.visualization_type === 'osa_by_category') {
        const columns = visualization.chart_params[0]?.main_category?.columns;
        const rows = visualization.chart_params[0]?.main_category?.rows;

        excelDataList = formatDataGridToExcel(columns, rows, visualization.chart_params[0].chart_target_x);
      } else if (visualization.visualization_type === 'column_grouped_table') {
        const columns = visualization.chart_params[0].columns;
        const rows = visualization.chart_params[0].rows;

        if (!columns.includes('store') && rows.some(row => row.store)) columns.unshift('store');
        excelDataList = formatDataGridToExcel(columns, rows);

        // Format the headers, so that they have the same format as the DataGrid table
        let firstHeader = ['']
        let secondHeader = [t('cws_app.dashboards.Store', 'Store')]
        excelDataList?.[0].forEach((column, index) => {
          const columnParts = column.split('.')
          if (columnParts.length > 1) {
            if (index % 2 === 0) {
              firstHeader.push('')
            } else {
              firstHeader.push(columnParts?.[1])
            }
            secondHeader.push(t(`cws_app.dashboards.${columnParts?.[0]}`, columnParts?.[0]))
          }
        })
        excelDataList = [firstHeader, secondHeader, ...excelDataList.slice(1)]
      } else if (visualization.visualization_type === 'row_grouped_table') {
        const columns = visualization.chart_params[0].columns;
        const rows = visualization.chart_params[0].rows;

        if (!columns.includes('store') && rows.some(row => row.store)) columns.unshift('store');
        excelDataList = formatDataGridToExcel(columns, rows, visualization.chart_params[0].chart_target_x);
      }
    }

    // Translate the header
    excelDataList[0] = excelDataList[0]?.map(header => t(`cws_app.dashboards.${header}`, header))

    return excelDataList;
  }

  return (
    <Grid container pt={4} px={2}>
      <Modal open={modalOpen} setOpen={setModalOpen} title={t('cws_app.dashboards.downloadModalTitle')}
        description={t('cws_app.dashboards.downloadModalDescription')} >
        <Button variant='contained' sx={{ backgroundColor: 'gray' }} onClick={() => handleIndividualDownload(false)}>{t('cws_app.dashboards.dashboardData')}</Button>
        <Button variant='contained' color='secondary' onClick={() => handleIndividualDownload(true)}>{t('cws_app.dashboards.rawData')}</Button>
      </Modal>

      <Modal open={generalModalOpen} setOpen={setGeneralModalOpen} title={t('cws_app.dashboards.downloadModalTitle')}
        description={t('cws_app.dashboards.downloadModalDescription')} >
        <Button variant='contained' sx={{ backgroundColor: 'gray' }} onClick={() => handleDownload(false)}>{t('cws_app.dashboards.dashboardData')}</Button>
        <Button variant='contained' color='secondary' onClick={() => handleDownload(true)}>{t('cws_app.dashboards.rawData')}</Button>

      </Modal>
      <Grid container spacing={2}>
        {/* Title and subtitle */}
        <Grid container item justifyContent='space-between'>
          <Grid item>
            <Typography variant="h4">{dashboardInfo?.dashboardTitle ? t(`cws_app.dashboards.${dashboardInfo?.dashboardTitle}`, dashboardInfo?.dashboardTitle) : ''}</Typography>
            <Typography variant="subtitle1" noWrap sx={{ textAlign: 'start', fontSize: 'small', width: '100%' }}>{dashboardInfo?.dashboardSubtitle ? t(`cws_app.dashboards.${dashboardInfo?.dashboardSubtitle}`, dashboardInfo?.dashboardSubtitle) : ''}</Typography>
          </Grid>


          {/* Download button */}
          {!isEmptyOrUndefined(selectedStores, 'array') && !isEmptyOrUndefined(dashboardInfo, 'object') &&
            <Grid item>
              <LoadingButton variant='contained' onClick={() => setGeneralModalOpen(true)}>
                <Grid container alignItems='center' spacing={1} >
                  <Grid item xs>
                    <IconComponent iconName={'download-outline'} style={{ fontSize: '18px' }} />
                  </Grid>
                  <Grid item xs>
                    <Typography>{t('cws_app.general.download', 'Download')}</Typography>
                  </Grid>
                </Grid>
              </LoadingButton>
            </Grid>
          }
        </Grid>
        {/* Filters */}
        <Grid container item spacing={1}>
          {/* Date filter */}
          <Grid item>
            {isEmptyOrUndefined(dateState, 'array') ?
              <LinearProgress />
              :
              <DateRange inputDateState={dateState} inputSetDateState={setDateState} days_limitation={90} clientFormats={clientFormats} />
            }
          </Grid>
          {/* Chain filter */}
          {/* Only show the chain filter if there's more than 1 chain */}
          {(isOSADashboard || isOldOSADashboard) && userChains?.length > 1 &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <Selector
                disabled={userChains?.length > 0 ? false : true}
                options={userChains}
                value={selectedChain}
                setValue={setSelectedChain}
                label={t('cws_app.general.sel_chain', 'Chain')}
                name='chain_name'
                loading={isLoadingUserStores}
              />
            </Grid>}
          {/* Store filter */}
          <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
            <MultiSelector
              disabled={filteredStores?.length > 0 ? false : true}
              options={filteredStores}
              groupBy={'chain_name'}
              value={selectedStores !== null ? selectedStores : []}
              setValue={setSelectedStores}
              label={t('cws_app.general.Stores', 'Stores')}
              name='parsed_name'
              tagName='store_code'
              loading={isLoadingUserStores}
              limitTags={(isOSADashboard || isOldOSADashboard) ? 1 : 7}
            />
          </Grid>
          {/* Supplier filter */}
          {/* Only show the supplier filter if the client is not a supplier */}
          {(isOSADashboard || isOldOSADashboard) && isEmptyOrUndefined(selectedClient?.supplier_id, 'id') &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                disabled={suppliers?.length > 0 ? false : true}
                options={suppliers}
                value={selectedSuppliers}
                setValue={setSelectedSuppliers}
                label={t('cws_app.general.Supplier', 'Supplier')}
                name='name'
                loading={isLoadingSuppliers}
                limitTags={1}
              />
            </Grid>}
          {/* Categories filter */}
          {(isOSADashboard || isOldOSADashboard) &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                disabled={categories?.categories?.length > 0 ? false : true}
                options={categories?.categories}
                value={selectedCategories}
                setValue={setSelectedCategories}
                label={t('cws_app.general.categories', 'Categories')}
                name='category_name'
                loading={isLoadingCategories}
                limitTags={1}
              />
            </Grid>}
          {/* SubCategories filter */}
          {(isOSADashboard || isOldOSADashboard) &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                disabled={filteredSubCategories?.length > 0 ? false : true}
                options={filteredSubCategories}
                value={selectedSubCategories}
                setValue={setSelectedSubCategories}
                label={t('cws_app.general.Sub-Categories', 'Sub-Categories')}
                name='category_name'
                loading={isLoadingCategories}
                limitTags={1}
              />
            </Grid>}
          {/* TODO: delete when old OSA report is deprecated */}
          {/* Store types filter */}
          {/* Only for Jumbo and Zippedi clients */}
          {isOldOSADashboard && (selectedClient?.client_id === ZIPPEDI_CLIENT_ID || selectedClient?.client_id === JUMBO_CLIENT_ID) &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                disabled={store_types?.length > 0 ? false : true}
                options={store_types}
                value={selectedStoreType}
                setValue={setSelectedStoreType}
                label={t('cws_app.general.Store type', 'Store type')}
                name='parsed_name'
                limitTags={2}
              />
            </Grid>}
          {/* Stock category */}
          {isOSADashboard &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                options={stockCategories}
                value={selectedStockCategories}
                setValue={setSelectedStockCategories}
                label={t('cws_app.general.Stock_category', 'Stock Category')}
                name='stock_name'
                loading={isLoadingStockCategories}
                limitTags={1}
              />
            </Grid>}
          {/* Data Source */}
          {isOSADashboard &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                options={lastProductSources}
                value={selectedDataSource}
                setValue={setSelectedDataSource}
                label={t('cws_app.general.Source', 'Source')}
                name='source_name'
                loading={isLoadingLastProductSources}
                limitTags={1}
              />
            </Grid>}
          {/* Product status */}
          {isOSADashboard &&
            <Grid item minWidth='10em' sx={{ flexGrow: 1 }}>
              <MultiSelector
                options={productStatus}
                value={selectedProductStatus}
                setValue={setSelectedProductStatus}
                label={t('cws_app.general.Select_Product_Status', 'Status')}
                name='status_name'
                loading={isLoadingCategories}
                limitTags={1}
              />
            </Grid>}

        </Grid>
        {/* Loading bar */}
        {isLoadingDashboard ?
          <LinearProgress sx={{ width: '100%', my: 2 }} color="secondary" />
          :
          <Grid container item>
            {/* Tables and charts */}
            {isEmptyOrUndefined(selectedStores, 'array') ?
              <Paper component={Grid} container item justifyContent='center'>
                <AlertBox
                  content={t('cws_app.general.select_information_to_display', 'Select information to display')}
                />
              </Paper>
              :
              (isEmptyOrUndefined(dashboardInfo?.visualizations, 'array') && !isEmptyOrUndefined(selectedStores, 'array')) || (dashboardInfo?.visualizations?.length === 1 && dashboardInfo?.visualizations[0]?.visualization_type === 'osa_kpi_metric') ?
                <Paper component={Grid} container item justifyContent='center'>
                  {/* No data to display */}
                  <AlertBox
                    content={t('cws_app.general.No_data', 'No data to display.')}
                  />
                </Paper>
                :
                <Grid container justifyContent='space-between'>
                  {dashboardInfo?.visualizations?.flatMap((visualization, index) => {
                    const md = visualization?.visualization_params?.charts_per_row ? 5.95 : 12;

                    return (
                      <Visualization
                        key={index}
                        md={md}
                        visualization={visualization}
                        isLoadingExcelDownload={isLoadingExcelDownload}
                        checkDownloadDisabled={checkDownloadDisabled}
                        setSelectedVisualization={setSelectedVisualization}
                        setModalOpen={setModalOpen}
                        isLoadingDashboard={isLoadingDashboard}
                        handleScroll={handleScroll}
                        filteredStores={filteredStores}
                        scrollPosition={scrollPosition}
                        OSAVerticalBarClickedLegend={OSAVerticalBarClickedLegend}
                        setOSAVerticalBarClickedLegend={setOSAVerticalBarClickedLegend}
                      />
                    )
                  })}
                </Grid>
            }
          </Grid >
        }
      </Grid >

    </Grid >
  );
}
